import i18n from "@/i18n";
import { IFilter } from "@/models/tables";
import { DataModules } from "@/store/modules/data";
import { FilterOperators, FilterTypes } from "@/data/table";
import store from "@/store";
import {
  ITemplateChannel,
  ITemplateRecipientType,
  ITemplateCategory,
  ITemplate
} from "@/models/templates";
import _ from "lodash";
import eventsMap from "@/components/app/global/timelineComponent/events";

export const IdParam = (id: ITemplate["id"]) => ({ "filter[id]": id });

export type CachedFiltertype = {
  cache: boolean;
};

export const NameFilter: () => IFilter = () => ({
  key: "name",
  label: i18n.t("_.name"),
  type: FilterTypes.STRING,
  operator: FilterOperators.EQUAL
});

export const CreatedAtFilter: () => IFilter = () => ({
  key: "created_at",
  label: i18n.t("_.created"),
  type: FilterTypes.DATE,
  operator: FilterOperators.EQUAL
});

export const DefaultFilter: () => IFilter = () => ({
  key: "default",
  label: i18n.t("_._default"),
  type: FilterTypes.SELECT_RADIO,
  value: "1",
  operator: FilterOperators.EQUAL
});

export const EnabledFilter: () => IFilter = () => ({
  key: "enabled",
  label: i18n.t("_.enabled"),
  type: FilterTypes.SELECT_RADIO,
  value: "1",
  operator: FilterOperators.EQUAL
});

let __categoriesCache: Promise<ITemplateCategory[]> | undefined = undefined;

export const CategoryFilter: (params?: CachedFiltertype) => IFilter = (
  params: CachedFiltertype = { cache: true }
) => {
  const getCategories = () =>
    store.dispatch(`data/${DataModules.NOTIFICATIONS_CATEGORIES}/list`, {
      storeData: false,
      params: {
        limit: 0,
        order: "name",
        with: "hooks"
      }
    });

  const optionsFunc = async () => {
    // Get data from cache
    __categoriesCache =
      params?.cache && __categoriesCache ? __categoriesCache : getCategories();

    const categories = await __categoriesCache;

    return _.map(categories, category => {
      return { value: category.id.toString(), label: category.name };
    });
  };

  return {
    key: "category_id",
    label: i18n.t("_.category"),
    type: FilterTypes.SELECT,
    optionsFunc,
    operator: FilterOperators.EQUAL
  };
};

let __channelsCache: Promise<ITemplateChannel[]> | undefined = undefined;

export const ChannelCodeFilter: (params?: CachedFiltertype) => IFilter = (
  params: CachedFiltertype = { cache: true }
) => {
  const getChannels = () =>
    store.dispatch(`data/${DataModules.NOTIFICATIONS_CHANNELS}/list`, {
      storeData: false,
      params: {
        order: "id",
        limit: 0
      }
    });

  const optionsFunc = async () => {
    // Get data from cache
    __channelsCache =
      params?.cache && __channelsCache ? __channelsCache : getChannels();

    const channels = await __channelsCache;

    return _.map(channels, channel => {
      return { value: channel.code, label: channel.name };
    });
  };

  return {
    key: "channel.code",
    label: i18n.t("_.channel"),
    type: FilterTypes.SELECT,
    optionsFunc,
    operator: FilterOperators.EQUAL
  };
};

let __recipientsCache:
  | Promise<ITemplateRecipientType[]>
  | undefined = undefined;

export const RecipientFilter: (params?: CachedFiltertype) => IFilter = (
  params: CachedFiltertype = { cache: true }
) => {
  const getRecipientTypes = () =>
    store.dispatch(`data/${DataModules.NOTIFICATIONS_CATEGORIES}/list`, {
      storeData: false,
      params: {
        limit: 0,
        order: "name",
        with: "hooks"
      }
    });

  const optionsFunc = async () => {
    // Get data from cache
    __recipientsCache =
      params?.cache && __recipientsCache
        ? __recipientsCache
        : getRecipientTypes();

    const recipientTypes = await __recipientsCache;

    return _.map(recipientTypes, recipientType => {
      return {
        value: recipientType.id.toString(),
        label: recipientType.name
      };
    });
  };

  return {
    key: "recipient_type_id",
    label: i18n.t("_.recipient_type"),
    type: FilterTypes.SELECT,
    optionsFunc,
    operator: FilterOperators.EQUAL
  };
};

export const MappingHookFilter: (params?: CachedFiltertype) => IFilter = (
  params: CachedFiltertype = { cache: true }
) => {
  const getCategories = () =>
    store.dispatch(`data/${DataModules.NOTIFICATIONS_CATEGORIES}/list`, {
      storeData: false,
      params: {
        limit: 0,
        order: "name",
        with: "hooks"
      }
    });

  const optionsFunc = async () => {
    // Get data from cache
    __categoriesCache =
      params?.cache && __categoriesCache ? __categoriesCache : getCategories();

    const categories = await __categoriesCache;

    const hookOptions: any[] = [];

    _.forEach(categories, category => {
      hookOptions.push({
        name: category.name,
        options: _.orderBy(
          (category.hooks || []).map(hook => {
            return {
              value: hook.id.toString(),
              label: eventsMap?.[hook?.code]?.name || hook.name
            };
          }),
          "label",
          "asc"
        )
      });
    });

    return hookOptions;
  };

  return {
    key: "mappings.hook_id",
    label: i18n.t("_.hook"),
    type: FilterTypes.SELECT,
    optionsFunc,
    operator: FilterOperators.EQUAL
  };
};

export const DefaultTemplateFilters: (
  params?: CachedFiltertype
) => IFilter[] = (params: CachedFiltertype = { cache: true }) => {
  return [
    NameFilter(),
    CreatedAtFilter(),
    DefaultFilter(),
    EnabledFilter(),
    CategoryFilter(params),
    ChannelCodeFilter(params),
    RecipientFilter(params),
    MappingHookFilter(params)
  ];
};

export const DefaultClientAreaTemplateFilters: (
  params?: CachedFiltertype
) => IFilter[] = () => {
  return [NameFilter(), CreatedAtFilter(), DefaultFilter()];
};

export const DisposeFilters = () => {
  __categoriesCache = undefined;
  __channelsCache = undefined;
  __recipientsCache = undefined;
};
